import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import NotificationBell from '../components/NotificationBell';
import { StarDisplayCompact } from '../components/StarRating';
import DashboardAnalytics from '../components/DashboardAnalytics';
import { offlineStorage } from '../utils/mediaUtils';
import {
  Plus,
  Search,
  LogOut,
  FileText,
  Calendar,
  MapPin,
  ChevronRight,
  ClipboardList,
  CheckCircle2,
  AlertCircle,
  Shield,
  ToggleLeft,
  ToggleRight,
  Lock,
  Upload,
  BarChart3,
  Wifi,
  WifiOff,
  Cloud,
  CloudOff,
  XCircle,
  Edit,
  Eye,
  Send,
  RotateCcw
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  draft: { label: 'Draft', class: 'status-draft', icon: FileText },
  pending_review: { label: 'Pending Review', class: 'status-pending', icon: AlertCircle },
  approved: { label: 'Approved', class: 'status-approved', icon: CheckCircle2 },
  rejected: { label: 'Rejected', class: 'status-rejected', icon: XCircle }
};

export default function DashboardPage() {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [restoring, setRestoring] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const fileInputRef = useRef(null);
  const { user, logout, getAuthHeaders, isMaster, toggleRole, getEffectiveRole, canToggleRole, isDualRole } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchInspections();
    checkOfflineDrafts();
    
    // Online/offline detection
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  const checkOfflineDrafts = async () => {
    const count = await offlineStorage.getPendingSyncCount();
    setPendingSyncCount(count);
  };

  const fetchInspections = async () => {
    try {
      const response = await axios.get(`${API_URL}/inspections`, {
        headers: getAuthHeaders()
      });
      setInspections(response.data);
    } catch (error) {
      toast.error('Failed to load inspections');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    navigate('/inspection/new');
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };

  const handleToggleRole = async () => {
    try {
      const newRole = await toggleRole();
      toast.success(`Switched to ${newRole === 'field_qc_engineer' ? 'QC Engineer' : 'Review Engineer'} mode`);
      // Refresh inspections after role change
      fetchInspections();
    } catch (error) {
      toast.error('Failed to toggle role');
    }
  };

  const handleRestoreClick = () => {
    fileInputRef.current?.click();
  };

  const handleRestoreFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.json')) {
      toast.error('Please select a valid backup file (.json)');
      return;
    }

    setRestoring(true);
    try {
      const text = await file.text();
      const backupData = JSON.parse(text);

      // Validate backup data structure
      if (!backupData.inspection || !backupData.backup_version) {
        toast.error('Invalid backup file format');
        return;
      }

      // Send to restore API
      const response = await axios.post(
        `${API_URL}/inspections/restore`,
        backupData,
        { headers: getAuthHeaders() }
      );

      toast.success(`Report "${response.data.report_id}" restored successfully!`);
      fetchInspections(); // Refresh the list
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else if (error instanceof SyntaxError) {
        toast.error('Invalid JSON file');
      } else {
        toast.error('Failed to restore backup');
      }
    } finally {
      setRestoring(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Quick action handlers
  const handleQuickEdit = (e, inspectionId) => {
    e.stopPropagation();
    navigate(`/inspection/${inspectionId}/edit`);
  };

  const handleQuickView = (e, inspectionId) => {
    e.stopPropagation();
    navigate(`/inspection/${inspectionId}`);
  };

  const handleQuickSubmit = async (e, inspectionId) => {
    e.stopPropagation();
    try {
      await axios.post(`${API_URL}/inspections/${inspectionId}/submit`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Submitted for review');
      fetchInspections();
    } catch (error) {
      toast.error('Failed to submit');
    }
  };

  const filteredInspections = inspections.filter(inspection => {
    const matchesSearch = 
      inspection.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inspection.project_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inspection.report_id.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || inspection.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: inspections.length,
    draft: inspections.filter(i => i.status === 'draft').length,
    pending: inspections.filter(i => i.status === 'pending_review').length,
    approved: inspections.filter(i => i.status === 'approved').length
  };

  const effectiveRole = getEffectiveRole ? getEffectiveRole() : user?.role;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header - Mobile Optimized */}
      <header className="bg-slate-900 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <img 
                src="/mypropmate-logo.png" 
                alt="MyPropMate Logo" 
                className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
              />
              <div>
                <h1 className="text-sm sm:text-lg font-heading font-bold tracking-tight">MYPROPMATE</h1>
                <p className="text-[10px] sm:text-xs text-slate-400 uppercase tracking-widest hidden sm:block">MYONK INFRASOLUTIONS</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Master Badge & Controls */}
              {isMaster && isMaster() && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/admin')}
                  className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 px-2 sm:px-3"
                >
                  <Shield className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Admin</span>
                </Button>
              )}
              
              {/* Toggle Role Button - Shows for Master OR Dual-Role users */}
              {canToggleRole && canToggleRole() && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleToggleRole}
                  className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 px-2 flex items-center gap-1"
                  title={`Switch to ${effectiveRole === 'field_qc_engineer' ? 'Review Engineer' : 'QC Engineer'}`}
                >
                  {effectiveRole === 'field_qc_engineer' ? (
                    <ToggleLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                  ) : (
                    <ToggleRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                  <span className="hidden sm:inline text-xs">
                    {effectiveRole === 'field_qc_engineer' ? 'QC' : 'Review'}
                  </span>
                </Button>
              )}
              
              {/* Analytics Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAnalytics(true)}
                className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 px-2"
                title="View Analytics"
              >
                <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
              
              {/* Online/Offline Status */}
              <div className={`px-2 py-1 rounded-full flex items-center gap-1 text-xs ${
                isOnline ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
              }`}>
                {isOnline ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                <span className="hidden sm:inline">{isOnline ? 'Online' : 'Offline'}</span>
              </div>
              
              {/* Pending Sync Indicator */}
              {pendingSyncCount > 0 && (
                <div className="px-2 py-1 rounded-full bg-amber-500/20 text-amber-400 flex items-center gap-1 text-xs">
                  <CloudOff className="w-3 h-3" />
                  <span>{pendingSyncCount}</span>
                </div>
              )}
              
              <NotificationBell />
              <div className="hidden md:flex items-center gap-3 text-sm">
                {user?.photo ? (
                  <img src={user.photo} alt={user.name} className="w-8 h-8 rounded-full object-cover border-2 border-slate-600" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white font-semibold text-sm">
                    {user?.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                )}
                <div className="flex flex-col">
                  <span className="text-slate-300 font-medium">{user?.name}</span>
                  {isMaster && isMaster() ? (
                    <span className="text-purple-400 text-xs uppercase">
                      Master ({effectiveRole === 'field_qc_engineer' ? 'QC' : 'Review'})
                    </span>
                  ) : isDualRole && isDualRole() ? (
                    <span className="text-cyan-400 text-xs uppercase">
                      Dual Role ({effectiveRole === 'field_qc_engineer' ? 'QC' : 'Review'})
                    </span>
                  ) : (
                    <span className="text-cyan-400 text-xs uppercase">
                      {user?.role === 'field_qc_engineer' ? 'Field QC' : 'Reviewer'}
                    </span>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-slate-400 hover:text-white hover:bg-slate-800 px-2"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Mobile Optimized */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Page Title & Actions */}
        <div className="flex flex-col gap-3 sm:gap-4 mb-4 sm:mb-8">
          <div>
            <h2 className="text-xl sm:text-2xl font-heading font-bold text-slate-900">Inspections</h2>
            <p className="text-sm sm:text-base text-slate-500 mt-1">
              {effectiveRole === 'field_qc_engineer' 
                ? 'Manage your property inspection reports' 
                : 'Review and approve inspection reports'}
            </p>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Restore backup button - visible to master users */}
            {isMaster && isMaster() && (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleRestoreFile}
                  accept=".json"
                  className="hidden"
                  data-testid="restore-file-input"
                />
                <Button
                  onClick={handleRestoreClick}
                  disabled={restoring}
                  variant="outline"
                  size="sm"
                  className="border-purple-500 text-purple-600 hover:bg-purple-50 text-xs sm:text-sm px-2 sm:px-4"
                  data-testid="restore-btn"
                >
                  <Upload className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">{restoring ? 'Restoring...' : 'Restore Backup'}</span>
                  <span className="sm:hidden">{restoring ? '...' : 'Restore'}</span>
                </Button>
              </>
            )}
            
            {effectiveRole === 'field_qc_engineer' && (
              <Button
                onClick={handleCreateNew}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-9 sm:h-11 px-3 sm:px-6 font-semibold text-sm sm:text-base"
                data-testid="create-inspection-btn"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">New Inspection</span>
                <span className="sm:hidden">New</span>
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards - Mobile Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-8">
          <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 bg-slate-100 rounded-sm">
                <ClipboardList className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-heading font-bold text-slate-900">{stats.total}</p>
                <p className="text-xs sm:text-sm text-slate-500">Total</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 bg-slate-100 rounded-sm">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-heading font-bold text-slate-900">{stats.draft}</p>
                <p className="text-xs sm:text-sm text-slate-500">Drafts</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 bg-orange-100 rounded-sm">
                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-heading font-bold text-slate-900">{stats.pending}</p>
                <p className="text-xs sm:text-sm text-slate-500">Pending</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 bg-emerald-100 rounded-sm">
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-heading font-bold text-slate-900">{stats.approved}</p>
                <p className="text-xs sm:text-sm text-slate-500">Approved</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filters - Mobile Optimized */}
        <div className="flex flex-col gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 sm:pl-10 h-10 sm:h-11 rounded-sm border-slate-200 text-sm sm:text-base"
              data-testid="search-input"
            />
          </div>
          
          {/* Filter buttons - horizontal scroll on mobile */}
          <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
            {['all', 'draft', 'pending_review', 'approved', 'rejected'].map((status) => (
              <Button
                key={status}
                variant={filterStatus === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus(status)}
                className={`rounded-sm capitalize whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 flex-shrink-0 ${filterStatus === status ? 'bg-slate-900' : ''}`}
                data-testid={`filter-${status}`}
              >
                {status === 'all' ? 'All' : status === 'pending_review' ? 'Pending' : status.replace('_', ' ')}
              </Button>
            ))}
          </div>
        </div>

        {/* Inspections List - Mobile Optimized */}
        {loading ? (
          <div className="flex items-center justify-center py-12 sm:py-20">
            <div className="spinner w-6 h-6 sm:w-8 sm:h-8"></div>
          </div>
        ) : filteredInspections.length === 0 ? (
          <div className="text-center py-12 sm:py-20 bg-white border border-slate-200 rounded-sm px-4">
            <ClipboardList className="w-10 h-10 sm:w-12 sm:h-12 text-slate-300 mx-auto mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-2">No inspections found</h3>
            <p className="text-sm sm:text-base text-slate-500 mb-4 sm:mb-6">
              {searchQuery || filterStatus !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'Create your first inspection report to get started'}
            </p>
            {effectiveRole === 'field_qc_engineer' && !searchQuery && filterStatus === 'all' && (
              <Button
                onClick={handleCreateNew}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-sm sm:text-base"
                data-testid="empty-create-btn"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                New Inspection
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {filteredInspections.map((inspection) => {
              const StatusIcon = statusConfig[inspection.status]?.icon || FileText;
              return (
                <div
                  key={inspection.id}
                  onClick={() => navigate(`/inspection/${inspection.id}`)}
                  className="inspection-card group bg-white border border-slate-200 rounded-sm p-3 sm:p-4 cursor-pointer hover:border-cyan-300 hover:shadow-md transition-all active:scale-[0.99]"
                  data-testid={`inspection-card-${inspection.id}`}
                >
                  <div className="flex flex-col gap-3">
                    {/* Status and ID row */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`status-pill ${statusConfig[inspection.status]?.class} text-xs px-2 py-0.5`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig[inspection.status]?.label}
                        </span>
                        <span className="text-[10px] sm:text-xs text-slate-400 font-mono">{inspection.report_id}</span>
                      </div>
                      {/* Lock indicator */}
                      {inspection.locked_by && (
                        <span className="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 bg-orange-100 text-orange-700 text-[10px] sm:text-xs rounded-full">
                          <Lock className="w-3 h-3" />
                          <span className="hidden sm:inline">Locked by {inspection.locked_by_name}</span>
                          <span className="sm:hidden">Locked</span>
                        </span>
                      )}
                    </div>
                    
                    {/* Client and Project */}
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-slate-900 truncate">
                        {inspection.client_name}
                      </h3>
                      <p className="text-sm sm:text-base text-slate-600 truncate">{inspection.project_name}</p>
                    </div>
                    
                    {/* Meta info */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                        <span className="truncate max-w-[120px] sm:max-w-none">{inspection.property_address}</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                        {inspection.date_of_inspection}
                      </span>
                    </div>
                    
                    {/* Rating and Quick Actions row */}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                      <div className="flex items-center gap-2">
                        <StarDisplayCompact rating={inspection.overall_rating} size="xs" />
                        <span className="text-xs sm:text-sm text-slate-600 font-medium">
                          {inspection.overall_rating}/10
                        </span>
                      </div>
                      
                      {/* Quick Action Buttons - Mobile Friendly */}
                      <div className="flex items-center gap-1">
                        {/* View button */}
                        <button
                          onClick={(e) => handleQuickView(e, inspection.id)}
                          className="p-1.5 sm:p-2 rounded-full hover:bg-slate-100 text-slate-500 hover:text-cyan-600 transition-colors"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        
                        {/* Edit button - only for drafts and owned by user */}
                        {(inspection.status === 'draft' && inspection.created_by === user?.id) && (
                          <button
                            onClick={(e) => handleQuickEdit(e, inspection.id)}
                            className="p-1.5 sm:p-2 rounded-full hover:bg-slate-100 text-slate-500 hover:text-blue-600 transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        
                        {/* Submit button - only for drafts owned by user */}
                        {inspection.status === 'draft' && inspection.created_by === user?.id && (
                          <button
                            onClick={(e) => handleQuickSubmit(e, inspection.id)}
                            className="p-1.5 sm:p-2 rounded-full hover:bg-emerald-100 text-slate-500 hover:text-emerald-600 transition-colors"
                            title="Submit for Review"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        )}
                        
                        <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-cyan-500 transition-colors" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      
      {/* Analytics Modal */}
      <DashboardAnalytics 
        isOpen={showAnalytics} 
        onClose={() => setShowAnalytics(false)} 
      />
    </div>
  );
}
